import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ClassCreateInputSchema, type ClassCreateInput } from '@sophia/shared-schemas'
import { Drawer } from '@/components/ui/Drawer'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Field } from '@/components/ui/Field'
import { Textarea } from '@/components/ui/Textarea'
import { classesApi, classesQueryKey } from '../api'
import { extractApiError } from '@/lib/api'

interface Props {
  open: boolean
  onClose: () => void
}

export function CreateClassDrawer({ open, onClose }: Props) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ClassCreateInput>({
    resolver: zodResolver(ClassCreateInputSchema),
    defaultValues: { name: '', definition: '' },
  })

  const mutation = useMutation({
    mutationFn: classesApi.create,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: classesQueryKey.list() })
      reset()
      onClose()
      navigate(`/classes/${created.id}`)
    },
    onError: (err) => {
      const norm = extractApiError(err)
      setSubmitError(norm.message)
    },
  })

  function onClosed() {
    if (mutation.isPending) return
    reset()
    setSubmitError(null)
    onClose()
  }

  return (
    <Drawer
      open={open}
      onClose={onClosed}
      title="Nova classe"
      description="Identifique a classe e descreva-a. Sinais e exemplos vêm na próxima tela."
      width={560}
      closeOnOverlay={!mutation.isPending}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClosed} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit((data) => {
              setSubmitError(null)
              mutation.mutate(data)
            })}
            loading={mutation.isPending}
          >
            Criar e configurar
          </Button>
        </div>
      }
    >
      <form
        onSubmit={handleSubmit((data) => {
          setSubmitError(null)
          mutation.mutate(data)
        })}
        className="flex flex-col gap-5"
        noValidate
      >
        <Field
          label="Nome da classe"
          htmlFor="name"
          required
          hint="Ex: Audiência de Instrução, Sentença de Mérito, Sessão de Julgamento"
          error={errors.name?.message}
        >
          <Input id="name" autoFocus invalid={!!errors.name} {...register('name')} />
        </Field>

        <Field
          label="Definição"
          htmlFor="definition"
          hint="O que distingue essa classe? Vai virar contexto pro LLM gerar regex."
          error={errors.definition?.message}
        >
          <Textarea
            id="definition"
            rows={6}
            placeholder="Ex: Publicação que designa data e horário pra audiência de instrução e julgamento. Geralmente menciona o juiz, sala e fórum."
            invalid={!!errors.definition}
            {...register('definition')}
          />
        </Field>

        {submitError && (
          <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2">
            {submitError}
          </div>
        )}
      </form>
    </Drawer>
  )
}
